import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, Search, Loader2, ExternalLink, ShoppingCart, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Purchase-material modal — searches Vinyl Wrapping Australia's Shopify
 * store, lets the user pick a variant + quantity, then redirects the
 * browser tab to Shopify's hosted checkout URL. All calls go through our
 * FastAPI proxy so the storefront token never touches the browser.
 */
export default function PurchaseMaterialModal({ open, onClose, defaultQuery = "" }) {
  const [query, setQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ configured: true });
  const [selectedVariant, setSelectedVariant] = useState({}); // productId → variantId
  const [quantity, setQuantity] = useState({}); // productId → int
  const [buyingId, setBuyingId] = useState(null);
  const inputRef = useRef(null);
  const searchTimer = useRef(null);

  // Check that Shopify credentials are wired up + pre-load the default result
  // set the moment the modal opens.
  useEffect(() => {
    if (!open) return;
    setQuery(defaultQuery || "");
    (async () => {
      try {
        const { data: s } = await api.get("/shopify/status");
        setStatus(s);
        if (s.configured) await runSearch(defaultQuery || "");
      } catch {
        setStatus({ configured: false });
      }
      setTimeout(() => inputRef.current?.focus(), 60);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultQuery]);

  // 350ms debounced live search — typical Algolia-style feel.
  useEffect(() => {
    if (!open || !status.configured) return;
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { runSearch(query); }, 350);
    return () => clearTimeout(searchTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, open]);

  const runSearch = async (q) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/shopify/products?first=24${q ? `&q=${encodeURIComponent(q)}` : ""}`);
      setProducts(data.products || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Shopify search failed");
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const buy = async (product) => {
    const chosenId = selectedVariant[product.id]
      || (product.variants?.nodes || []).find((v) => v.availableForSale)?.id;
    if (!chosenId) return toast.error("No available variant to purchase");
    const qty = Math.max(1, Math.min(250, parseInt(quantity[product.id], 10) || 1));
    const chosen = (product.variants?.nodes || []).find((v) => v.id === chosenId);
    setBuyingId(product.id);
    try {
      const { data } = await api.post("/shopify/cart", {
        lines: [{ merchandise_id: chosenId, quantity: qty }],
        // Snapshot for auto-log inventory when "Mark received" is later clicked
        line_metadata: [{
          product_title: product.title,
          variant_title: chosen?.title || "",
          sku: chosen?.sku || "",
          price: parseFloat(chosen?.price?.amount || 0),
          currency: chosen?.price?.currencyCode || "AUD",
          vendor: product.vendor || "",
          image_url: product.featuredImage?.url || chosen?.image?.url || "",
          product_type: product.productType || "",
        }],
      });
      window.open(data.checkout_url, "_blank", "noopener,noreferrer");
      toast.success("Cart created — checkout opened in a new tab. Mark it received in Inventory once it arrives.");
      onClose?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail
          : detail?.userErrors?.[0]?.message
          || "Couldn't create Shopify cart"
      );
    } finally {
      setBuyingId(null);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="purchase-material-modal"
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-8 px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-4xl bg-slate-950 border border-white/10 rounded-3xl shadow-2xl p-6 md:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="text-emerald-300 text-[11px] uppercase tracking-widest">Vinyl Wrapping Australia</div>
            <h2 className="font-heading text-2xl md:text-3xl text-white mt-0.5">Purchase material</h2>
            <p className="text-slate-400 text-sm mt-1">
              Search the store, pick a size, and Shopify handles the checkout in a new tab.
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="purchase-material-close"
            className="p-2 text-slate-400 hover:text-white rounded-full hover:bg-white/5"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {!status.configured ? (
          <ShopifyNotConfigured />
        ) : (
          <>
            <div className="relative mb-4">
              <Search className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search PPF, vinyl, tint, tools…"
                data-testid="purchase-material-search"
                className="w-full bg-white/[0.04] border border-white/10 rounded-2xl pl-10 pr-10 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-400/40"
              />
              {loading && (
                <Loader2 className="w-4 h-4 text-slate-500 animate-spin absolute right-3.5 top-1/2 -translate-y-1/2" />
              )}
            </div>

            <div className="max-h-[60vh] overflow-y-auto pr-1 space-y-3" data-testid="purchase-material-results">
              {!loading && products.length === 0 ? (
                <div className="text-center py-16 text-slate-500 text-sm">
                  No products match “{query}”. Try a broader term like <span className="text-slate-300">vinyl</span> or <span className="text-slate-300">PPF</span>.
                </div>
              ) : (
                products.map((p) => (
                  <ProductRow
                    key={p.id}
                    product={p}
                    selectedVariantId={selectedVariant[p.id]}
                    onVariant={(vid) => setSelectedVariant({ ...selectedVariant, [p.id]: vid })}
                    quantity={quantity[p.id] || 1}
                    onQuantity={(q) => setQuantity({ ...quantity, [p.id]: q })}
                    onBuy={() => buy(p)}
                    busy={buyingId === p.id}
                  />
                ))
              )}
            </div>

            <div className="text-xs text-slate-500 mt-4 flex items-center gap-1.5 justify-end">
              <ShoppingCart className="w-3 h-3" /> Checkout is hosted on
              <a href="https://vinylwrappingaustralia.com" target="_blank" rel="noopener noreferrer" className="text-slate-300 hover:text-white inline-flex items-center gap-1">
                vinylwrappingaustralia.com <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ShopifyNotConfigured() {
  return (
    <div className="text-center py-12 space-y-3">
      <div className="mx-auto w-12 h-12 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
        <AlertTriangle className="w-6 h-6 text-amber-300" />
      </div>
      <div className="text-white font-medium">Shopify isn't connected yet</div>
      <div className="text-slate-400 text-sm max-w-md mx-auto">
        Add <code className="text-slate-200">SHOPIFY_STORE_DOMAIN</code> and
        {" "}<code className="text-slate-200">SHOPIFY_STOREFRONT_ACCESS_TOKEN</code> to the backend .env,
        then restart the backend to enable Purchase material.
      </div>
    </div>
  );
}

function ProductRow({ product, selectedVariantId, onVariant, quantity, onQuantity, onBuy, busy }) {
  const variants = product.variants?.nodes || [];
  const availableVariants = useMemo(() => variants.filter((v) => v.availableForSale), [variants]);
  const chosen = variants.find((v) => v.id === selectedVariantId)
    || availableVariants[0]
    || variants[0];
  const priceText = chosen
    ? `$${Number(chosen.price?.amount || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} ${chosen.price?.currencyCode || ""}`
    : "—";
  const canBuy = chosen?.availableForSale;

  return (
    <div
      data-testid={`purchase-material-product-${product.handle}`}
      className="bg-white/[0.03] border border-white/10 rounded-2xl p-3 md:p-4 flex gap-4 items-start"
    >
      {product.featuredImage?.url ? (
        <img
          src={product.featuredImage.url}
          alt={product.featuredImage.altText || product.title}
          className="w-20 h-20 md:w-24 md:h-24 rounded-xl object-cover border border-white/10 shrink-0"
        />
      ) : (
        <div className="w-20 h-20 md:w-24 md:h-24 rounded-xl bg-white/[0.04] border border-white/10 shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-white font-medium truncate">{product.title}</h3>
            <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
              {product.vendor && <span>{product.vendor}</span>}
              {product.productType && <span className="px-1.5 py-0.5 rounded-full bg-white/5">{product.productType}</span>}
            </div>
          </div>
          <div className="text-emerald-300 font-semibold text-lg shrink-0">{priceText}</div>
        </div>

        {variants.length > 1 && (
          <select
            value={chosen?.id || ""}
            onChange={(e) => onVariant(e.target.value)}
            data-testid={`purchase-material-variant-${product.handle}`}
            className="w-full mt-2 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-400/40"
          >
            {variants.map((v) => (
              <option key={v.id} value={v.id} disabled={!v.availableForSale}>
                {v.title} — ${Number(v.price?.amount || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                {v.availableForSale ? "" : " (sold out)"}
              </option>
            ))}
          </select>
        )}

        <div className="flex items-center gap-2 mt-3">
          <input
            type="number"
            min={1}
            max={250}
            value={quantity}
            onChange={(e) => onQuantity(parseInt(e.target.value, 10) || 1)}
            data-testid={`purchase-material-qty-${product.handle}`}
            className="w-20 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white text-center focus:outline-none focus:border-emerald-400/40"
          />
          <button
            onClick={onBuy}
            disabled={!canBuy || busy}
            data-testid={`purchase-material-buy-${product.handle}`}
            className="flex-1 flex items-center justify-center gap-2 bg-emerald-500 text-black text-sm font-semibold rounded-full py-2 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating cart…</>
              : canBuy ? <><ShoppingCart className="w-4 h-4" /> Buy on Shopify</>
              : "Sold out"}
          </button>
        </div>
      </div>
    </div>
  );
}
